import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import UIkit from "uikit";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";

import RecordsPerPage from "../../../common/RecordsPerPage";
import OpsSubscriptionRequired, {
  hasOpsSubscription,
} from "../../../common/OpsSubscriptionRequired";
import Pagination from "../../../common/Pagination";
import Tags from "../../../common/Tags";
import Dialog from "../../../common/Dialog";
import StorageLocationAdd from "./add";
import { AppContexts } from "../../../../providers";
import StorageService from "../../../../services/storage.service";
import StorageMenu from "../menu";
import {
  DEFAULT_RPP,
  INITIAL_LOCATIONS,
  LOCATION_TYPE_OPTIONS,
  STATUS_FILTER_OPTIONS,
  labelFor,
  usageLabels,
} from "../data";

export default function StorageLocationsIndex() {
  const [cookies] = useCookies(["records-per-page"]);
  const { user: account } = useContext(AppContexts.AuthContext);
  const settingsContext = useContext(AppContexts.SettingsContext);
  const opsSubscriptionLocked = !hasOpsSubscription(account);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [locations, setLocations] = useState(INITIAL_LOCATIONS);
  const [editingLocation, setEditingLocation] = useState(null);
  const [deletingLocation, setDeletingLocation] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [criteria, setCriteria] = useState({
    search: "",
    type: "",
    status: "",
    page: 0,
  });
  const getSettings = settingsContext.get;

  const loadLocations = useCallback(async () => {
    try {
      const resp = await StorageService.listLocations({ per: 1000 });
      setLocations(resp.data?.paginated || []);
    } catch (err) {
      UIkit.notification("Failed to load storage locations.", "error");
    }
  }, []);

  useEffect(() => {
    document.title = "Storage Locations - RueBaRue";
    getSettings(["tags"]);
    loadLocations();
  }, [getSettings, loadLocations]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page: ["search", "type", "status"].includes(key)
        ? 0
        : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const handleSelectedTags = useCallback((tags, location) => {
    setLocations((prev) =>
      prev.map((loc) => (loc.id === location.id ? { ...loc, tags } : loc)),
    );
  }, []);

  const toggleActive = useCallback((locId) => {
    setLocations((prev) =>
      prev.map((loc) =>
        loc.id === locId ? { ...loc, active: !loc.active } : loc,
      ),
    );
  }, []);

  const openAdd = useCallback(() => {
    setEditingLocation(null);
    UIkit.offcanvas("#modal-storage-location-add").show();
  }, []);

  const openEdit = useCallback((location) => {
    setEditingLocation(location);
    UIkit.offcanvas("#modal-storage-location-add").show();
  }, []);

  const handleSaveLocation = useCallback(async (payload) => {
    setIsProcessing(true);
    try {
      const resp = payload.id
        ? await StorageService.updateLocation(payload.id, payload)
        : await StorageService.createLocation(payload);
      const saved = resp.data;

      setLocations((prev) => {
        if (payload.id) {
          return prev.map((location) =>
            location.id === payload.id ? saved : location,
          );
        }

        return [saved, ...prev];
      });

      UIkit.notification("Storage location saved", "success");
      return true;
    } catch (err) {
      UIkit.notification("Failed to save storage location.", "error");
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const openDelete = useCallback((location) => {
    setDeletingLocation(location);
  }, []);

  const handleDeleteLocation = useCallback(async () => {
    if (!deletingLocation) return;

    setIsProcessing(true);
    try {
      await StorageService.deleteLocation(deletingLocation.id);
      setLocations((prev) => prev.filter((loc) => loc.id !== deletingLocation.id));
      UIkit.modal("#modal-storage-location-delete").hide();
      setDeletingLocation(null);
      UIkit.notification("Storage location deleted", "success");
    } catch (err) {
      UIkit.notification("Failed to delete storage location.", "error");
    } finally {
      setIsProcessing(false);
    }
  }, [deletingLocation]);

  const cancelDelete = useCallback(() => {
    setDeletingLocation(null);
  }, []);

  const filtered = useMemo(() => {
    return locations.filter((loc) => {
      if (criteria.type && loc.location_type !== criteria.type) return false;
      if (criteria.status === "active" && !loc.active) return false;
      if (criteria.status === "inactive" && loc.active) return false;

      if (criteria.search) {
        const query = criteria.search.toLowerCase();
        const tagNames = (loc.tags || []).map((tag) => tag.name).join(" ");
        const haystack = `${loc.name} ${loc.code || ""} ${loc.address || ""} ${tagNames}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });
  }, [locations, criteria]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / recordsPerPage));
  const pageLocations = filtered.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage,
  );

  return (
    <>
      <section id="dashboard-template">
        <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
          <h1 className="uk-heading-small uk-margin-remove">Storage Locations</h1>
        </div>

        {opsSubscriptionLocked && <OpsSubscriptionRequired />}

        <div id="hms-main-body">
          <StorageMenu />
          <div className="uk-flex uk-flex-between uk-flex-top">
            <button
              className="uk-button hms-btn hms-red-btn"
              type="button"
              onClick={openAdd}
              disabled={opsSubscriptionLocked}
              title={opsSubscriptionLocked ? "Ops Subscription Required" : undefined}
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Location
            </button>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <div className="uk-inline uk-margin-small-right">
                    <div className="uk-search uk-search-default uk-form-custom">
                      {criteria.search ? (
                        <button
                          type="button"
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() => handleCriteriaChange("search", "")}
                          aria-label="Clear search"
                        ></button>
                      ) : (
                        <span className="uk-search-icon-flip" uk-search-icon="true" />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Search location, code, tags"
                        value={criteria.search}
                        onChange={(event) =>
                          handleCriteriaChange("search", event.target.value)
                        }
                      />
                    </div>
                  </div>

                  {[
                    ["type", LOCATION_TYPE_OPTIONS, "All Types"],
                    ["status", STATUS_FILTER_OPTIONS, "All Statuses"],
                  ].map(([key, options, fallback]) => (
                    <div
                      key={key}
                      className="uk-inline uk-margin-small-right"
                      uk-form-custom="target: > * > span:first-child"
                    >
                      <select
                        value={criteria[key]}
                        onChange={(event) =>
                          handleCriteriaChange(key, event.target.value)
                        }
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                        <span className="select-value">
                          {criteria[key] ? labelFor(options, criteria[key]) : fallback}
                        </span>
                        <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                      </button>
                    </div>
                  ))}
                </form>
              </Flex>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th>Active</th>
                    <th className="uk-table-expand">Location</th>
                    <th>Type</th>
                    <th>Count</th>
                    <th>Usage</th>
                    <th>Tags</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageLocations.length === 0 && (
                    <tr>
                      <td colSpan={7}>
                        <div className="uk-text-center uk-padding-small uk-text-meta">
                          No storage locations found. Adjust your filters.
                        </div>
                      </td>
                    </tr>
                  )}

                  {pageLocations.map((loc) => (
                    <tr key={loc.id}>
                      <td>
                        <div className="autosend-checkbox">
                          <label className="setting-switch">
                            <input
                              type="checkbox"
                              checked={!!loc.active}
                              onChange={() => toggleActive(loc.id)}
                            />
                            <span className="setting-slider setting-round"></span>
                          </label>
                        </div>
                      </td>
                      <td className="semi-bold-font">
                        {loc.name}
                        <div className="uk-text-meta">{loc.code || "No code"}</div>
                      </td>
                      <td>{labelFor(LOCATION_TYPE_OPTIONS, loc.location_type)}</td>
                      <td>{loc.count_required ? "Required" : "Optional"}</td>
                      <td>{usageLabels(loc.usage)}</td>
                      <td className="hms-add-tag">
                        <Tags
                          value={loc.tags || []}
                          onChange={(tags) => handleSelectedTags(tags, loc)}
                        />
                      </td>
                      <td className="hms-action-btn">
                        <div className="uk-inline">
                          <button
                            className="uk-button"
                            type="button"
                            onClick={() => openEdit(loc)}
                            uk-tooltip="Edit"
                          >
                            <img src="/images/edit.svg" alt="Edit" />
                          </button>
                          <button
                            className="uk-button"
                            type="button"
                            uk-toggle="target: #modal-storage-location-delete"
                            onClick={() => openDelete(loc)}
                            disabled={isProcessing}
                            uk-tooltip="Delete"
                          >
                            <img src="/images/delete.svg" alt="Delete" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <Flex alignment="between top" className="guest-form-top-filters">
                <Pagination
                  count={totalPages}
                  index={Math.min(criteria.page, totalPages - 1)}
                  onIndexChanged={handleIndexChanged}
                />
                <RecordsPerPage />
              </Flex>
            </div>
          </div>
        </div>
      </section>

      <StorageLocationAdd
        location={editingLocation}
        flagIsNew={!editingLocation}
        flagIsProcessing={isProcessing}
        onSave={handleSaveLocation}
        onCancel={() => setEditingLocation(null)}
      />

      <Dialog id="modal-storage-location-delete">
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
            onClick={cancelDelete}
          ></button>
          <h3 className="uk-modal-title">Delete Location</h3>
          <p>
            Are you sure you want to delete <b>{deletingLocation?.name}</b>?
          </p>
          <p className="uk-text-right">
            <button
              className="uk-button hms-btn uk-modal-close uk-modal-delete-cancel"
              type="button"
              onClick={cancelDelete}
            >
              Cancel
            </button>
            <button
              className="uk-button hms-btn hms-gray-btn"
              type="button"
              onClick={handleDeleteLocation}
              disabled={isProcessing}
            >
              Delete
            </button>
          </p>
        </div>
      </Dialog>
    </>
  );
}
